import { GoogleGenAI, GenerateContentResponse, Type } from "@google/genai";
import { AIService } from "../types";

export class GeminiService implements AIService {
  /**
   * Generates content for a workflow node execution.
   * Uses 'gemini-3-flash-preview' as the default model for efficient processing.
   * Re-instantiates the client on each call to ensure process.env.API_KEY is current.
   */
  async generateWorkflowCompletion(prompt: string, model: string = 'gemini-3-flash-preview'): Promise<string> {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response: GenerateContentResponse = await ai.models.generateContent({
        model: model,
        contents: prompt,
      });

      // The response.text is a property, not a method.
      return response.text || "";
    } catch (error) {
      console.error("Gemini API Error:", error);
      throw new Error("Failed to generate content from Gemini.");
    }
  }

  /**
   * Evaluates a logical condition using Gemini's structured output.
   * Configures responseSchema for reliable boolean results.
   *
   * Throws on API failure, missing credentials, or malformed responses.
   * Previous versions silently returned a random boolean on error, which
   * fabricated simulation results; callers are expected to catch rejections.
   */
  async evaluateCondition(context: Record<string, unknown>, condition: string): Promise<boolean> {
     if (!process.env.API_KEY) {
       throw new Error(
         "GeminiService.evaluateCondition: API_KEY is not configured. " +
         "Set process.env.API_KEY or inject a different AIService implementation."
       );
     }

     const prompt = `
      Context: ${JSON.stringify(context)}
      Condition to evaluate: ${condition}
      Return ONLY valid JSON: {"result": boolean}
     `;

     const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
     const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: prompt,
        config: {
            responseMimeType: "application/json",
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                result: {
                  type: Type.BOOLEAN,
                  description: 'The evaluated boolean result'
                }
              },
              required: ['result']
            }
        }
     });

     const raw = response.text || "";
     try {
       const json = JSON.parse(raw);
       return !!json.result;
     } catch (parseError) {
       throw new Error(
         `GeminiService.evaluateCondition: failed to parse model response as JSON. ` +
         `Raw response: ${raw.slice(0, 200)}`
       );
     }
  }
}

export const geminiService = new GeminiService();